// DefaultConfirmation.jsx
'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import IconComplete from '../../../client/img/icon-complete.svg';
import IconInfo from '../../../client/img/icon-info.svg';
import IconError from '../../../client/img/icon-error.svg';
import IconWarning from '../../../client/img/icon-warning.svg';
import media from '../style/media.js';
import { color } from '../style/variables.js';
import { ToastrType } from '../resource/popupNotificationConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

export const DefaultConfirmation = ({
  type = ToastrType.SUCCESS,
  title = '',
  content = '',
  primaryButtonText = '',
  primaryButtonTrackingId = '',
  onPrimaryButtonClick = NULL_FUNCTION,
  secondaryButtonText = '',
  secondaryButtonTrackingId = '',
  onSecondaryButtonClick = NULL_FUNCTION,
  status = 'hidden',
  shouldShowIcon = true,
}) => {
  const { source, description } =
    type === ToastrType.SUCCESS
      ? { source: IconComplete, description: 'notification-icon-complete' }
      : type === ToastrType.ERROR
        ? { source: IconError, description: 'notification-icon-error' }
        : type === ToastrType.WARNING
          ? { source: IconWarning, description: 'notification-icon-warning' }
          : { source: IconInfo, description: 'notification-icon-info' };

  return (
    <ToastWrapper>
      <StyledNotification $type={type} $status={status}>
        <NotificationContent>
          {shouldShowIcon && (
            <IconWrapper $type={type}>
              <img src={source} alt={description} />
            </IconWrapper>
          )}
          <TextContent>
            <Title $isContent={content.length > 0}>{title}</Title>
            {content && <Content>{content}</Content>}
          </TextContent>
        </NotificationContent>
        <ButtonGroup>
          {primaryButtonText && onPrimaryButtonClick && (
            <PrimaryButton
              $type={type}
              data-element_id={primaryButtonTrackingId}
              onClick={onPrimaryButtonClick}
            >
              {primaryButtonText}
            </PrimaryButton>
          )}
          {secondaryButtonText && onSecondaryButtonClick && (
            <SecondaryButton
              data-element_id={secondaryButtonTrackingId}
              onClick={onSecondaryButtonClick}
            >
              {secondaryButtonText}
            </SecondaryButton>
          )}
        </ButtonGroup>
      </StyledNotification>
    </ToastWrapper>
  );
};

DefaultConfirmation.propTypes = {
  type: PropTypes.oneOf([
    ToastrType.INFO,
    ToastrType.ERROR,
    ToastrType.SUCCESS,
  ]),
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  status: PropTypes.string,
  primaryButtonText: PropTypes.string,
  primaryButtonTrackingId: PropTypes.string,
  onPrimaryButtonClick: PropTypes.func,
  secondaryButtonText: PropTypes.string,
  secondaryButtonTrackingId: PropTypes.string,
  onSecondaryButtonClick: PropTypes.func,
  shouldShowIcon: PropTypes.bool,
};

const getColorByType = type => {
  switch (type) {
    case 'complete':
      return `${color.tealBlue}`; // Brand color (success)
    case 'info':
      return `${color.sky}`; // Blue (info/warning)
    case 'error':
      return `${color.red}`; // Red (error)
    case 'warning':
      return `${color.yellow}`; // Yellow (warning)
    default:
      return `${color.tealBlue}`;
  }
};

// Container to simulate different screen sizes
const StyledNotification = styled.div`
  width: 100%;
  max-width: 528px;
  min-height: 48px;
  max-height: 164px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 2px 8px ${color.neutral['0_alpha_10']};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* 平板：寬度最小336px，最大528px */
  ${media.tablet`
    max-width: 528px
    min-width: 336px
  `}

  /* 手機：延展寬度，左右邊界各12px (總24px) */
  ${media.mobile`
   max-width: 100%;
  `}
`;

const ToastWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  margin: 0 12px;
`;

const NotificationContent = styled.div`
  display: flex;
  padding: 12px 16px;
  position: relative;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-right: 12px;
  padding-top: 2px;
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: end;
  flex-direction: column;
  overflow: hidden;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.black};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: ${props => (props.$isContent ? '4px' : '0')};
`;

const Content = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${color.black};
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 12px;
  gap: 8px;
  min-height: 38px;
`;

const BaseButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PrimaryButton = styled(BaseButton)`
  background-color: ${({ $type }) => getColorByType($type)};
  padding: 2px 22px;
  color: ${({ $type }) =>
    $type === ToastrType.WARNING ? color.neutral[0] : color.white};
  font-size: 12px;
  &:hover {
    opacity: 0.9;
  }
`;

const SecondaryButton = styled(BaseButton)`
  background-color: transparent;
  color: ${color.black};
  font-size: 12px;
  padding: 6px 12px;
  &:hover {
    background-color: ${color.neutral[95]};
  }
`;

export default DefaultConfirmation;
